import { createFileRoute } from "@tanstack/react-router";
import { Mail, MessageCircle, Building2 } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { alternateLinkList } from "@/lib/i18n/locale";

export const Route = createFileRoute("/vi/contact")({
  head: () => ({
    meta: [
      { title: "Liên hệ — banana.cards" },
      { name: "description", content: "Trao đổi với đội ngũ banana.cards về gói doanh nghiệp, đối tác hoặc hỗ trợ." },
      { property: "og:title", content: "Liên hệ banana.cards" },
      { property: "og:description", content: "Chúng tôi phản hồi trong vòng 1 ngày làm việc." },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/contact", "vi"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Liên hệ banana.cards",
          description: "Trao đổi với đội ngũ banana.cards về gói doanh nghiệp, đối tác hoặc hỗ trợ.",
          url: "https://banana.cards/vi/contact",
          mainEntity: {
            "@type": "Organization",
            name: "Banana Cards",
            url: "https://banana.cards",
            email: "hello@banana.cards",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Building A1, Dubai Digital Park, Dubai Silicon Oasis",
              addressLocality: "Dubai",
              addressCountry: "AE",
            },
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Liên hệ</p>
              <h1 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
                Hãy <em className="italic">trò chuyện.</em>
              </h1>
              <p className="mt-6 max-w-md text-base text-muted-foreground md:text-lg">
                Dù bạn triển khai Banana cho 10 người hay 10.000 người, đội ngũ của chúng tôi luôn sẵn sàng hỗ trợ.
              </p>
              <div className="mt-10 space-y-4">
                {[
                  { icon: Building2, title: "Công ty", desc: "SIX STUDIO CREATIVE - FZCO" },
                  { icon: Mail, title: "Liên hệ chung", desc: "hello@banana.cards" },
                  { icon: MessageCircle, title: "Hỗ trợ", desc: "Chat 24/7 trong ứng dụng." },
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.title} className="flex items-start gap-4 rounded-2xl bg-muted p-5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-card">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="text-sm font-bold">{c.title}</p>
                        <p className="text-sm text-muted-foreground">{c.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 rounded-2xl bg-muted p-5">
                <p className="text-sm font-bold">Địa chỉ văn phòng</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Building A1, Dubai Digital Park<br />
                  Dubai Silicon Oasis, Dubai<br />
                  Các Tiểu vương quốc Ả Rập Thống nhất
                </p>
              </div>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); alert("Cảm ơn — chúng tôi sẽ phản hồi trong 1 ngày làm việc."); }}
              className="rounded-3xl bg-muted p-8"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Tên"><input required className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
                <Field label="Họ"><input required className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
              </div>
              <div className="mt-4">
                <Field label="Email công việc"><input required type="email" className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Công ty"><input className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
                <Field label="Quy mô đội">
                  <select className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10">
                    <option>Chỉ mình tôi</option><option>2–10</option><option>11–50</option><option>51–250</option><option>250+</option>
                  </select>
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Chúng tôi có thể giúp gì?"><textarea rows={4} className="w-full rounded-lg bg-card px-3 py-2.5 text-sm ring-1 ring-foreground/10 focus:ring-foreground/30 focus:outline-none" /></Field>
              </div>
              <button className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Gửi tin nhắn
              </button>
            </form>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}